import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  List,
  ListItemButton,
  Stack,
  Typography,
} from '@mui/material';

import { EntityContext } from '../../entity';
import { getWebhookDelivery, listWebhookDeliveries } from '../../services/webhookOutboxApi';
import {
  HISTORY_NOTICE,
  LOAD_ERROR,
  attemptsLabel,
  emptyMessage,
  mergePages,
  prettyJson,
  resultLabel,
  statusColor,
  statusLabel,
} from './labels';

/**
 * CHAN-70: histórico de webhooks de um envelope (`envelopeKey`) ou de um lote
 * (`batchEnvelopeKey`). Só leitura: sem reenvio (fica para história futura).
 *
 * - Carrega ao abrir e quando o usuário clica em "Atualizar"; nada de polling (RN-09).
 * - Nada do payload ou da resposta vai para localStorage nem para analytics.
 */
export default function WebhookDeliveriesPanel({ envelopeKey, batchEnvelopeKey }) {
  const { entity } = useContext(EntityContext);
  const kind = batchEnvelopeKey ? 'batch' : 'envelope';

  const [items, setItems] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [selected, setSelected] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const apiKey = entity?.api_key;
  // Só a resposta da requisição mais recente vale: troca de lote/envelope/entidade ou
  // clique em outra entrega descartam respostas atrasadas da anterior.
  const listSeq = useRef(0);
  const detailSeq = useRef(0);

  const load = useCallback(
    async (nextCursor) => {
      if (!apiKey || (!envelopeKey && !batchEnvelopeKey)) return;
      listSeq.current += 1;
      const seq = listSeq.current;
      setLoading(true);
      setError(false);
      try {
        const data = await listWebhookDeliveries(apiKey, {
          envelopeKey,
          batchEnvelopeKey,
          cursor: nextCursor || undefined,
        });
        if (seq !== listSeq.current) return;
        const page = data?.items || [];
        setItems((prev) => (nextCursor ? mergePages(prev, page) : page));
        setCursor(data?.next_cursor || null);
        if (!nextCursor) {
          // "Atualizar": o detalhe aberto passa a refletir a linha nova da lista.
          setSelected((prev) => (prev ? page.find((i) => i.outbox_key === prev.outbox_key) || prev : prev));
        }
      } catch (e) {
        if (seq !== listSeq.current) return;
        if (e?.response?.status === 404) {
          // Envelope/lote de outra entidade ou inexistente: a API não revela qual; o
          // usuário só não vê nenhuma entrega (Cenário 9), sem "Tentar novamente".
          setItems([]);
          setCursor(null);
        } else {
          setError(true);
        }
      } finally {
        if (seq === listSeq.current) setLoading(false);
      }
    },
    [apiKey, envelopeKey, batchEnvelopeKey]
  );

  useEffect(() => {
    // Outro envelope/lote/entidade: nada da consulta anterior pode continuar na tela.
    detailSeq.current += 1;
    setItems([]);
    setCursor(null);
    setSelected(null);
    setDetailLoading(false);
    load(null);
  }, [load]);

  const openDetail = async (item) => {
    detailSeq.current += 1;
    const seq = detailSeq.current;
    setSelected(item);
    setDetailLoading(true);
    try {
      const detalhe = await getWebhookDelivery(apiKey, item.outbox_key);
      if (seq === detailSeq.current) setSelected(detalhe);
    } catch (e) {
      // Fica com o que veio na lista (mesmo formato).
    } finally {
      if (seq === detailSeq.current) setDetailLoading(false);
    }
  };

  if (error && items.length === 0) {
    return (
      <Stack spacing={2} sx={{ p: 2 }}>
        <Alert severity="error">{LOAD_ERROR}</Alert>
        <Box>
          <Button variant="outlined" onClick={() => load(null)}>
            Tentar novamente
          </Button>
        </Box>
      </Stack>
    );
  }

  return (
    <Stack spacing={2} sx={{ p: 2 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Typography variant="caption" color="text.secondary">
          {HISTORY_NOTICE}
        </Typography>
        <Button size="small" onClick={() => load(null)} disabled={loading} aria-label="Atualizar webhooks">
          Atualizar
        </Button>
      </Stack>

      {loading && items.length === 0 ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress size={28} aria-label="Carregando webhooks" />
        </Box>
      ) : null}

      {!loading && items.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
          {emptyMessage(kind)}
        </Typography>
      ) : null}

      {items.length > 0 ? (
        <List disablePadding aria-label="Entregas de webhook">
          {items.map((item) => (
            <ListItemButton
              key={item.outbox_key}
              selected={selected?.outbox_key === item.outbox_key}
              onClick={() => openDetail(item)}
              aria-label={`Webhook ${item.event_type}, ${statusLabel(item)}`}
              sx={{ flexDirection: 'column', alignItems: 'stretch', borderBottom: 1, borderColor: 'divider' }}
            >
              <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                <Typography variant="subtitle2">{item.event_type}</Typography>
                <Chip size="small" color={statusColor(item)} label={statusLabel(item)} />
              </Stack>
              <Typography variant="caption" color="text.secondary" noWrap title={item.endpoint_url || ''}>
                {item.endpoint_url}
              </Typography>
              <Stack direction="row" spacing={2} sx={{ mt: 0.5 }}>
                <Typography variant="caption">Tentativas: {attemptsLabel(item)}</Typography>
                <Typography variant="caption">
                  HTTP: {item.last_status_code ?? '—'}
                </Typography>
                <Typography variant="caption">Última: {item.last_attempt_at || '—'}</Typography>
              </Stack>
            </ListItemButton>
          ))}
        </List>
      ) : null}

      {error && items.length > 0 ? <Alert severity="error">{LOAD_ERROR}</Alert> : null}

      {cursor ? (
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <Button variant="outlined" onClick={() => load(cursor)} disabled={loading}>
            {loading ? 'Carregando…' : 'Carregar mais'}
          </Button>
        </Box>
      ) : null}

      {selected ? (
        <Box component="section" aria-label="Detalhe do webhook">
          <Divider sx={{ my: 1 }} />
          <Stack spacing={1}>
            <Typography variant="subtitle1">{selected.event_type}</Typography>
            <Typography variant="body2">
              {statusLabel(selected)} · tentativas {attemptsLabel(selected)} · último resultado: {resultLabel(selected)}
            </Typography>
            {selected.origin === 'replay' ? (
              <Typography variant="caption" color="text.secondary">
                Reenvio da entrega {selected.replay_of}
              </Typography>
            ) : null}
            {detailLoading ? <CircularProgress size={18} aria-label="Carregando detalhe" /> : null}
            <Typography variant="overline">Payload enviado</Typography>
            <Box component="pre" sx={preStyle}>
              {prettyJson(selected.payload)}
            </Box>
            <Typography variant="overline">Resposta do seu servidor</Typography>
            <Box component="pre" sx={preStyle}>
              {selected.last_response_body ? prettyJson(selected.last_response_body) : 'Sem resposta registrada'}
            </Box>
          </Stack>
        </Box>
      ) : null}
    </Stack>
  );
}

const preStyle = {
  m: 0,
  p: 1.5,
  bgcolor: 'grey.100',
  borderRadius: 1,
  fontSize: 12,
  maxHeight: 280,
  overflow: 'auto',
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
};

WebhookDeliveriesPanel.propTypes = {
  envelopeKey: PropTypes.string,
  batchEnvelopeKey: PropTypes.string,
};
